import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown } from "lucide-react";

interface Option {
  label: string;
  value: string | number;
}

interface SelectedDoctor {
  name: string;
  mappingId: string | number;
}

interface SearchableDropdownProps {
  options?: Option[];
  onChange: (option: Option) => void;
  value?: string;
  placeholder?: string;
  selectedDoctor?: SelectedDoctor;
  doctorsData?: any[];
  className?: string;
  /** 🆕 message shown when no results are found */
  noOptionsMessage?: string;
  /** 🆕 placeholder for the search input */
  searchPlaceholder?: string;
  /** 🆕 disable search functionality */
  disableSearch?: boolean;
  /** 🆕 disable text truncation */
  disableTruncation?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options = [],
  onChange,
  value,
  placeholder = "Select an option",
  selectedDoctor = { name: "", mappingId: "" },
  doctorsData = [],
  className,
  noOptionsMessage = "No options available",
  searchPlaceholder = "Search...",
  disableSearch = false,
  disableTruncation = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (disableSearch || !searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, disableSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current && !disableSearch) {
      searchInputRef.current.focus();
    }
  }, [isOpen, disableSearch]);

  const handleOptionSelect = (option: Option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const getDisplayValue = () => {
    if (options?.length === 0) {
      return noOptionsMessage;
    }

    if (!disableTruncation && selectedDoctor.name.length > 8) {
      return `${selectedDoctor.name.substring(0, 8)}...`;
    }

    return selectedDoctor.name || placeholder;
  };

  return (
    <div
      ref={dropdownRef}
      className={`relative w-full md:w-[180px] ${className || ""}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer flex items-center justify-between"
        type="button"
      >
        <span className={`${!disableTruncation ? "truncate" : ""} text-gray-700`}>{getDisplayValue()}</span>
        <ChevronDown
          className={`ml-2 h-4 w-4 text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
          {!disableSearch && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder} // ✅ dynamic
                  className="w-full rounded-md border border-gray-200 bg-white pl-10 pr-3 py-2 text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 h-8"
                />
              </div>
            </div>
          )}

          <div className="max-h-[150px] overflow-y-auto side_bar">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? `${noOptionsMessage}` : noOptionsMessage}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value || index}
                  onClick={() => handleOptionSelect(option)}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700 focus:outline-none transition-colors duration-150"
                  type="button"
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
